import { useMemo, createElement } from 'react';
import styles from './SlackText.module.css';

/* Slack mrkdwn, rendered.

   Announcements (and support replies) are mirrored out of Slack verbatim, so
   the text the api stores still carries Slack's own markers. Dropping that
   string into a <p> is what put the literal asterisks on screen — this turns
   the markers back into the emphasis they stand for.

   Slack's flavour, not CommonMark: a single asterisk is *bold* (not italic),
   _underscores_ are italic, ~tildes~ strike through, backticks are code, and
   there are no headings or real lists. Links arrive already unwrapped by the
   api (decodeSlackText turns <url|label> into "label (url)"), so bare URLs are
   linkified here rather than parsed out of Slack's angle-bracket form. */

const MARKERS = { '*': 'strong', _: 'em', '~': 'del', '`': 'code' };

// Slack only treats a marker as formatting on a word boundary — the rule that
// leaves snake_case_names, 3*4 and a lone asterisk alone.
const isWordChar = (ch) => !!ch && /[\p{L}\p{N}_]/u.test(ch);

const runLength = (text, i, ch) => {
  let n = 0;
  while (text[i + n] === ch) n++;
  return n;
};

/* The closing run for a marker opened at `from`, or -1. A run qualifies when
   it doesn't follow a space (Slack won't close on "* "), leaves the span
   non-empty, and isn't buried inside a word. A blank line ends the search:
   a stray asterisk shouldn't reach across paragraphs to find a partner. */
function findClose(text, from, ch, open) {
  for (let j = from; j < text.length; j++) {
    if (text[j] === '\n' && text[j + 1] === '\n') return -1;
    if (text[j] !== ch) continue;
    if (j === from) continue;                       // empty span
    if (/\s/.test(text[j - 1])) continue;           // "…word *" isn't a closer
    if (isWordChar(text[j + runLength(text, j, ch)])) continue;
    return j;
  }
  return -1;
}

// A bare URL keeps its trailing sentence punctuation out of the href — most of
// all the ")" from the "label (url)" shape the api hands over.
const URL_RE = /\bhttps?:\/\/[^\s<>]+/g;

function linkify(text, key) {
  const out = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const href = match[0].replace(/[.,;:!?'")\]]+$/, '');
    if (!href) continue;
    if (match.index > last) out.push(text.slice(last, match.index));
    out.push(
      <a key={`${key}-a${match.index}`} href={href} target="_blank" rel="noreferrer" className={styles.link}>
        {href}
      </a>,
    );
    last = match.index + href.length;
  }
  if (!out.length) return [text];
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function parseInline(text, key) {
  const out = [];
  let plain = '';
  const flush = () => {
    if (!plain) return;
    out.push(...linkify(plain, `${key}-${out.length}`));
    plain = '';
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const tag = MARKERS[ch];
    if (!tag) { plain += ch; continue; }

    // A run, so a markdown habit like **bold** reads as one delimiter rather
    // than an empty span followed by a stray asterisk.
    const open = runLength(text, i, ch);
    const after = text[i + open];
    if (isWordChar(text[i - 1]) || !after || /\s/.test(after)) { plain += ch; continue; }

    const close = findClose(text, i + open, ch, open);
    if (close === -1) { plain += ch; continue; }

    const inner = text.slice(i + open, close);
    flush();
    out.push(
      createElement(
        tag,
        { key: `${key}-${i}`, className: tag === 'code' ? styles.code : undefined },
        // Nothing formats inside a code span.
        tag === 'code' ? inner : parseInline(inner, `${key}-${i}`),
      ),
    );
    i = close + Math.min(open, runLength(text, close, ch)) - 1;
  }
  flush();
  return out;
}

const QUOTE_RE = /^\s*>\s?/;

/* Fenced code first, so nothing inside one is read as a marker; then the
   quoted lines, which group into one block. Everything else stays a single
   run per stretch, because the container keeps white-space: pre-wrap and the
   author's own line breaks are the formatting. */
function toBlocks(source) {
  const parts = String(source ?? '').split('```');
  const blocks = [];

  parts.forEach((part, index) => {
    if (index % 2 === 1) {
      const code = part.replace(/^\n/, '').replace(/\n$/, '');
      if (code) blocks.push({ type: 'code', text: code });
      return;
    }

    let buffer = [];
    let quoting = false;
    const flush = () => {
      const text = buffer.join('\n');
      if (text.trim()) blocks.push({ type: quoting ? 'quote' : 'text', text });
      buffer = [];
    };

    for (const line of part.split('\n')) {
      const isQuote = QUOTE_RE.test(line);
      if (isQuote !== quoting) { flush(); quoting = isQuote; }
      buffer.push(isQuote ? line.replace(QUOTE_RE, '') : line);
    }
    flush();
  });

  return blocks;
}

/* `as` is the wrapper element. It defaults to a div, but a caller that renders
   into phrasing content (the help centre drops a reply inside a <span>) passes
   "span" so the markup stays valid — the blocks then follow suit and lean on
   .para for their block layout instead of on <p>. */
function SlackText({ text, className, as: Root = 'div' }) {
  const blocks = useMemo(() => toBlocks(text), [text]);
  if (!blocks.length) return null;

  const inline = Root === 'span';
  const Para = inline ? 'span' : 'p';
  const Quote = inline ? 'span' : 'blockquote';

  return (
    <Root className={className ? `${styles.root} ${className}` : styles.root}>
      {blocks.map((block, i) => {
        if (block.type === 'code') {
          return (
            <code key={i} className={styles.codeBlock}>
              {block.text}
            </code>
          );
        }
        if (block.type === 'quote') {
          return (
            <Quote key={i} className={styles.quote}>
              {parseInline(block.text, `q${i}`)}
            </Quote>
          );
        }
        return (
          <Para key={i} className={styles.para}>
            {parseInline(block.text, `p${i}`)}
          </Para>
        );
      })}
    </Root>
  );
}

export default SlackText;
